import { useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, X, GripVertical } from "lucide-react";
import { API_BASE, getAuthToken, IS_MOCK_BACKEND, toBackendUrl } from "@/lib/api";
import { toast } from "sonner";

const MOCK_UPLOAD_MAX_BYTES = 900 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * GalleryUpload — multi-image uploader with thumbnail reorder + remove.
 *
 * Props:
 *   value: string[]
 *   onChange: (arr: string[]) => void
 *   max?: number (default 8)
 *   testId?: string
 */
export default function GalleryUpload({ value = [], onChange, max = 8, testId = "gallery-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const uploadFiles = async (files) => {
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} images allowed.`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setUploading(true);
    try {
      const token = getAuthToken();
      // Parallel upload — fail-fast on first error
      const uploads = list
        .filter((file) => {
          if (!file.type.startsWith("image/")) return false;
          if (file.size > 5 * 1024 * 1024) {
            toast.error(`${file.name}: too large (max 5 MB).`);
            return false;
          }
          if (IS_MOCK_BACKEND && file.size > MOCK_UPLOAD_MAX_BYTES) {
            toast.error(`${file.name}: keep under 900 KB for cPanel mock hosting.`);
            return false;
          }
          return true;
        })
        .map(async (file) => {
          if (IS_MOCK_BACKEND) {
            return fileToDataUrl(file);
          }
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await axios.post(`${API_BASE}/admin/upload`, fd, {
            withCredentials: true,
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          return toBackendUrl(data.url);
        });
      const results = await Promise.allSettled(uploads);
      const uploaded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failed = results.length - uploaded.length;
      if (failed > 0) {
        toast.error(`${failed} upload${failed > 1 ? "s" : ""} failed`);
      }
      if (uploaded.length) {
        onChange([...value, ...uploaded]);
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} added`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  // Reorder helpers
  const onDragStart = (idx) => (e) => {
    e.dataTransfer.setData("text/plain", String(idx));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
    setDragOverIdx(null);
    if (Number.isNaN(from) || from === idx) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">Gallery ({value.length}/{max})</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || value.length >= max}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--rsi-purple)] hover:gap-2 transition-all disabled:opacity-50"
          data-testid={`${testId}-add`}
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Add images
        </button>
      </div>
      {value.length === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-gray-300 rounded-xl p-5 text-center bg-gray-50 hover:bg-gray-100 transition-colors"
          data-testid={`${testId}-empty`}
        >
          <div className="text-xs text-gray-500">Click to add gallery images (PNG, JPG, WebP · up to 5 MB each)</div>
        </button>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {value.map((url, idx) => (
            <div
              key={url + idx}
              draggable
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDragLeave={() => setDragOverIdx(null)}
              onDrop={onDrop(idx)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 group ${
                dragOverIdx === idx ? "border-[var(--rsi-purple)]" : "border-gray-200"
              }`}
              data-testid={`${testId}-thumb-${idx}`}
            >
              <img src={url} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-x-1 top-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="h-6 w-6 rounded grid place-items-center bg-black/50 text-white cursor-grab" title="Drag to reorder">
                  <GripVertical className="h-3 w-3" />
                </span>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`${testId}-remove-${idx}`}
                  className="h-6 w-6 rounded grid place-items-center bg-white/90 text-red-600 hover:bg-white"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => uploadFiles(e.target.files || [])}
        data-testid={`${testId}-input`}
      />
      {uploading && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--rsi-purple)]" />
          Uploading…
        </div>
      )}
    </div>
  );
}
